/*
 * This file is part of Seyes.
 *
 * Copyright (C) 2025-2026 Yannick Defais <yannick.defais@ac-creteil.fr>
 *
 * Seyes is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Seyes is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with Seyes. If not, see <https://www.gnu.org/licenses/>.
 */

import { config } from "../config.mjs";
import { CLE_STOCKAGE_OPTIONS_PDF, POLICES_CURSIVES } from "./constantes.mjs";
import { journaliseur } from "./journaliseur.mjs";

/**
 * Attend que le navigateur ait layouté + peint la frame courante via un
 * double `requestAnimationFrame`. Pattern cross-browser sans `setTimeout`.
 *
 * - 1er rAF : enregistre le callback pour la prochaine frame.
 * - 2e rAF (imbriqué) : garantit qu'au moins une frame complète a été
 *   layoutée ET peinte entre-temps.
 *
 * Utile avant toute capture DOM (SnapDOM) pour que les modifications de
 * style/DOM précédentes soient visibles par la capture.
 *
 * Durée typique : ~16-32 ms (1 ou 2 refresh frames @ 60 Hz).
 *
 * @returns {Promise<void>}
 */
export function attendreFramesStables() {
	return new Promise((resolve) => {
		requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
	});
}

/**
 * Attend que le layout d'un élément soit stable : son `scrollHeight` ne change
 * plus sur N frames consécutives. Protège contre les reflows asynchrones (ex :
 * métriques typographiques qui finissent de s'appliquer après un changement
 * de `nombreDeCarreaux` ou de police).
 *
 * Condition-based wait (skill systematic-debugging) — préférable à un
 * `setTimeout` arbitraire.
 *
 * @param {HTMLElement} element
 * @param {Object} [options]
 * @param {number} [options.framesStables=3] - Nombre de frames consécutives stables
 * @param {number} [options.maxMs=600] - Timeout maximum (protège contre pages infinies)
 * @returns {Promise<{stable: boolean, frames: number, dureeMs: number}>}
 */
export async function attendreLayoutStable(element, { framesStables = 3, maxMs = 600 } = {}) {
	const debut = performance.now();
	let lastHeight = element.scrollHeight;
	let consecutivesStables = 0;
	let frames = 0;

	while (performance.now() - debut < maxMs) {
		await new Promise((r) => requestAnimationFrame(r));
		frames++;
		void element.offsetHeight; // force reflow sync
		const currentHeight = element.scrollHeight;
		if (currentHeight === lastHeight) {
			consecutivesStables++;
			if (consecutivesStables >= framesStables) {
				return { stable: true, frames, dureeMs: Math.round(performance.now() - debut) };
			}
		} else {
			consecutivesStables = 0;
			lastHeight = currentHeight;
		}
	}
	return { stable: false, frames, dureeMs: Math.round(performance.now() - debut) };
}

/**
 * Utilitaires pour la feature PDF.
 *
 * Converti depuis la classe UtilitairesSeyes (méthodes statiques) vers des
 * fonctions libres, conformément aux conventions upstream.
 *
 * Les dépendances window.jspdf / window.snapdom ne sont plus nécessaires
 * après la conversion ES : les modules consommateurs importent jsPDF et
 * snapdom via npm.
 */

/**
 * Normalise la largeur du carreau en entier cohérent avec le rendu CSS.
 *
 * INVARIANT seyes-pdf-guard #1 : la CSS utilise `Math.floor()` pour calculer
 * la valeur de référence du carreau. Tout module qui convertit la largeur
 * flottante en entier DOIT utiliser ce helper (et donc `Math.floor`), sinon
 * la divergence entraîne un décalage progressif du mapping Seyès par rapport
 * aux positions rendues (symptôme : coupures de paragraphes mal détectées,
 * pagination décalée, mots coupés en frontière de page).
 *
 * @param {number} largeurCarreauBrute - La valeur flottante lue depuis le CSS.
 * @returns {number} Entier via Math.floor, aligné avec le CSS.
 */
export function largeurCarreauNormalisee(largeurCarreauBrute) {
	return Math.floor(largeurCarreauBrute);
}

/**
 * Récupère la configuration Seyès courante depuis le DOM.
 *
 * @returns {{nombreDeCarreaux: number, largeur_carreau: number, largeur_marge: number}}
 */
export function obtenirConfigSeyes() {
	// Nombre de carreaux
	const inputCarreaux = document.getElementById("inputNombreDeCarreaux");
	const nombreDeCarreaux = inputCarreaux ? parseInt(inputCarreaux.value, 10) || 21 : 21;

	// Largeur du carreau depuis le background-size de #page
	const page = document.getElementById("page");
	let largeur_carreau = 80;
	if (page) {
		const bgSize = getComputedStyle(page).backgroundSize;
		const match = bgSize.match(/^([\d.]+)px/);
		if (match) {
			largeur_carreau = parseFloat(match[1]);
		}
	}

	// Largeur de la marge
	const marge = document.getElementById("marge");
	const largeur_marge = marge ? marge.offsetWidth : 200;

	return { nombreDeCarreaux, largeur_carreau, largeur_marge };
}

/**
 * Attend que les polices soient chargées, force un reflow, puis temporise.
 *
 * @returns {Promise<void>}
 */
export async function stabiliserPolices() {
	journaliseur.debug("UTILITAIRES", "Stabilisation des polices en cours...");

	if (document.fonts && document.fonts.ready) {
		await document.fonts.ready;
	}

	const elements = [
		document.getElementById("texte_principal"),
		document.getElementById("texte_marge"),
		document.getElementById("texte_marge_secondaire"),
	].filter(Boolean);

	elements.forEach((element) => {
		const familleOriginale = element.style.fontFamily;
		element.style.fontFamily = "Arial";

		element.offsetHeight; // Force un reflow
		element.style.fontFamily = familleOriginale;

		element.offsetHeight; // Force un autre reflow
	});

	// Double rAF au lieu de setTimeout(300 ms) — même garantie (paint terminé)
	// sans le surcoût d'attente fixe.
	await attendreFramesStables();

	journaliseur.debug("UTILITAIRES", "Polices stabilisées");
}

/**
 * Cache module-level des SVG de réglure convertis en data URL base64.
 * Clé = URL absolue d'origine. Valeur = data URL, ou `null` si fetch a échoué
 * (memoize l'échec aussi pour éviter les re-tentatives).
 *
 * @type {Map<string, string|null>}
 */
const _cacheBackgroundDataUrl = new Map();

async function _fetchEnDataUrl(url) {
	const resp = await fetch(url, { cache: "force-cache" });
	if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
	const blob = await resp.blob();
	return await new Promise((resolve, reject) => {
		const fr = new FileReader();
		fr.onload = () => resolve(String(fr.result || ""));
		fr.onerror = () => reject(fr.error || new Error("FileReader failed"));
		fr.readAsDataURL(blob);
	});
}

/**
 * Scanne le sous-arbre `racine` à la recherche d'éléments avec
 * `background-image: url(...)`, fetch chaque URL distincte et la convertit en
 * data URL base64. Retourne un `Map<urlOriginale, dataUrl>` utilisable ensuite
 * pour réécrire les `background-image` dans le clone capturé.
 *
 * **Pourquoi** (Bug Z, 2026-04-30, itération finale 2026-05-04) : SnapDOM v2
 * embarque un blanchiment de `background-image: url(...)` non inliné en `data:`
 * pendant la sérialisation finale du SVG (cf. snapdom.mjs : `m.includes("url(")
 * && !m.includes("data:") && (m="none")`). Si l'inliner async interne de
 * SnapDOM n'a pas réussi à pré-populer son cache (`C.background`) — fetch
 * timeout, course sur cache miss, ou restriction Firefox incognito — la
 * sérialisation produit `background-image: none`. Conséquence : aperçu OK
 * (rendu via `<img src=svgUrl>` dans la page hôte) mais canvas KO (Firefox
 * SVG-as-image rasterise sans réglure). Cf. `attendreReglureChargee` (HTTP
 * cache prefetch, insuffisant) puis `prechargerRessourcesSnapdom`/`preCache`
 * (cache interne SnapDOM, également insuffisant car l'inliner peut quand même
 * échouer en incognito).
 *
 * On bypass complètement la pipeline async de SnapDOM en pré-chargeant les
 * data URLs nous-même (`fetch` standard depuis le contexte page), puis en les
 * écrivant comme `style.backgroundImage = url("data:...")` directement sur le
 * clone via `appliquerBackgroundsInlinesDansClone`. Le filter SnapDOM voit
 * `data:` et conserve la valeur. Robuste à toutes les variantes du bug.
 *
 * Idempotent : cache module-level, hits instantanés après premier fetch.
 * Tolérant aux erreurs (URL inaccessible → memoize null, retourne map sans
 * cette URL → l'élément concerné n'aura pas de réglure mais l'export ne
 * plante pas).
 *
 * @param {Element|Document} [racine=document.body] - Sous-arbre à scanner.
 * @returns {Promise<Map<string,string>>} Map url originale → data URL.
 */
export async function chargerBackgroundImagesEnDataUrl(racine) {
	const racineEl = racine instanceof Element ? racine : document.body;
	const tous = [racineEl, ...racineEl.querySelectorAll("*")];
	const urls = new Set();
	for (const el of tous) {
		const bg = window.getComputedStyle(el).backgroundImage;
		if (!bg || bg === "none") continue;
		const matches = bg.matchAll(/url\((['"]?)([^'")]+)\1\)/g);
		for (const m of matches) {
			const u = m[2];
			// Skip data: URLs (déjà inlinées) et fragments internes (#…).
			if (!u.startsWith("data:") && !u.startsWith("#")) urls.add(u);
		}
	}
	if (urls.size === 0) {
		journaliseur.debug("UTILITAIRES", "Aucune background-image externe à inliner");
		return new Map();
	}

	const map = new Map();
	await Promise.all([...urls].map(async (url) => {
		if (_cacheBackgroundDataUrl.has(url)) {
			const cached = _cacheBackgroundDataUrl.get(url);
			if (cached) map.set(url, cached);
			return;
		}
		try {
			const dataUrl = await _fetchEnDataUrl(url);
			_cacheBackgroundDataUrl.set(url, dataUrl);
			map.set(url, dataUrl);
		} catch (e) {
			_cacheBackgroundDataUrl.set(url, null);
			journaliseur.avertir(
				"UTILITAIRES",
				`Échec inline data URL pour ${url}: ${e && e.message ? e.message : e}`,
			);
		}
	}));
	journaliseur.debug(
		"UTILITAIRES",
		`Backgrounds inlinés en data URL : ${map.size}/${urls.size}`,
	);
	return map;
}

function _echapperPourRegex(s) {
	return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Applique les data URLs collectés par `chargerBackgroundImagesEnDataUrl` à
 * tous les éléments du clone capturé qui ont un `background-image` référençant
 * l'une des URLs originales. Réécrit l'inline style avec la data URL pour que
 * le `getComputedStyle` retourné à SnapDOM contienne `data:` → SnapDOM ne
 * blanchit plus l'URL pendant la sérialisation.
 *
 * À appeler dans le hook `onclone` de `capturerAvecSnapDOM`, après
 * `normaliserCSSPourCapture` (les forçages `background-size` ne touchent pas
 * `background-image`).
 *
 * `racineCloneOuPseudoDoc` peut être un Element réel ou le pseudoDoc forwardé
 * par `capturerAvecSnapDOM` (qui implémente `querySelectorAll`).
 *
 * @param {Element|{querySelectorAll:Function,querySelector?:Function}} racineCloneOuPseudoDoc
 * @param {Map<string,string>} mapDataUrls
 * @returns {void}
 */
export function appliquerBackgroundsInlinesDansClone(racineCloneOuPseudoDoc, mapDataUrls) {
	if (!mapDataUrls || mapDataUrls.size === 0) return;
	if (!racineCloneOuPseudoDoc || typeof racineCloneOuPseudoDoc.querySelectorAll !== "function") return;

	const elements = Array.from(racineCloneOuPseudoDoc.querySelectorAll("*"));
	// Inclure aussi la racine cloneé du conteneur (querySelectorAll renvoie les
	// descendants seuls). Pour un Element réel passé en racine, l'inclure aussi.
	if (racineCloneOuPseudoDoc instanceof Element) {
		elements.unshift(racineCloneOuPseudoDoc);
	} else if (typeof racineCloneOuPseudoDoc.querySelector === "function") {
		const racineConteneur = racineCloneOuPseudoDoc.querySelector("#conteneur-extensible");
		if (racineConteneur) elements.unshift(racineConteneur);
	}

	let modifies = 0;
	for (const el of elements) {
		const cs = window.getComputedStyle(el);
		const bg = cs.backgroundImage;
		if (!bg || bg === "none" || !bg.includes("url(")) continue;

		let nouveau = bg;
		let modifie = false;
		for (const [orig, dataUrl] of mapDataUrls) {
			if (!nouveau.includes(orig)) continue;
			const re = new RegExp(`url\\((['"]?)${_echapperPourRegex(orig)}\\1\\)`, "g");
			const remplace = nouveau.replace(re, `url("${dataUrl}")`);
			if (remplace !== nouveau) {
				nouveau = remplace;
				modifie = true;
			}
		}
		if (modifie) {
			el.style.setProperty("background-image", nouveau, "important");
			modifies++;
		}
	}
	journaliseur.debug(
		"UTILITAIRES",
		`Inlining backgrounds dans clone : ${modifies} éléments mis à jour`,
	);
}

/**
 * Détecte si la police active (dans #choix_police ou sur #texte_principal)
 * est une police cursive, listée dans POLICES_CURSIVES.
 *
 * @returns {boolean}
 */
export function estPoliceCursive() {
	const selecteurPolice = document.getElementById("choix_police");
	let policeActive = (selecteurPolice && selecteurPolice.value) || null;

	if (!policeActive) {
		const textePrincipal = document.getElementById("texte_principal");
		if (textePrincipal) {
			const styleCalcule = window.getComputedStyle(textePrincipal);
			policeActive = styleCalcule.fontFamily.split(",")[0].replace(/['"]/g, "").trim();
		}
	}

	const estCursive = POLICES_CURSIVES.includes(policeActive);
	journaliseur.debug("UTILITAIRES", `Détection police cursive: ${policeActive}`, {
		police: policeActive,
		estCursive,
	});
	return estCursive;
}

/**
 * Génère un nom de fichier PDF par défaut : "seyes-DD-MM-YYYY.pdf" ou le
 * nom du fichier ouvert si disponible (config.nomDuFichierOuvert).
 *
 * @returns {string} Nom de fichier avec extension .pdf
 */
export function genererNomFichier() {
	let nom = "seyes";

	const dateDoc = config.dateDuDoc || null;
	if (dateDoc && typeof dateDoc === "string") {
		nom += `-${dateDoc.replace(/\s+/g, "-")}`;
	} else {
		const aujourdHui = new Date();
		const dateStr = aujourdHui.toLocaleDateString("fr-FR").replace(/\//g, "-");
		nom += `-${dateStr}`;
	}

	if (config.nomDuFichierOuvert) {
		nom = config.nomDuFichierOuvert;
	}

	if (!nom.endsWith(".pdf")) {
		nom += ".pdf";
	}

	journaliseur.debug("UTILITAIRES", `Nom fichier généré: ${nom}`);
	return nom;
}

/**
 * Charge les options PDF persistées depuis localStorage.
 *
 * Retourne un objet partiel (à merger sur les defaults) ou null si aucune
 * valeur n'a été stockée, si le JSON est invalide, ou si localStorage est
 * indisponible (mode privé, quota, etc.).
 *
 * L'appelant reste responsable de valider chaque champ contre son domaine
 * (defense-in-depth : données stockées potentiellement obsolètes).
 *
 * @returns {Object|null}
 */
export function chargerOptionsPDF() {
	try {
		const str = localStorage.getItem(CLE_STOCKAGE_OPTIONS_PDF);
		if (!str) return null;
		return JSON.parse(str);
	} catch (e) {
		journaliseur.debug("UTILITAIRES", `chargerOptionsPDF: échec lecture (${e.message})`);
		return null;
	}
}

/**
 * Sauvegarde les options PDF dans localStorage (silencieux en cas d'erreur).
 *
 * @param {Object} options - Objet à sérialiser en JSON
 */
export function sauvegarderOptionsPDF(options) {
	try {
		localStorage.setItem(CLE_STOCKAGE_OPTIONS_PDF, JSON.stringify(options));
	} catch (e) {
		journaliseur.debug("UTILITAIRES", `sauvegarderOptionsPDF: échec écriture (${e.message})`);
	}
}
